import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiSettings } from 'react-icons/fi';
import DialogBox from "./DialogBox";
import { solutionTypes, configuredSolutions as defaultSolutions, getSolutionTypeInfo } from "../../Data/Data";
import "./DashboardHeader.css";

const DashboardHeader = ({ currentSolutionType, settingsRoute }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [configuredSolutions, setConfiguredSolutions] = useState(defaultSolutions);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [managedTypes, setManagedTypes] = useState([]);

  const currentSolution = configuredSolutions.find(s => s.type === currentSolutionType);
  const currentTypeInfo = currentSolution ? getSolutionTypeInfo(currentSolution.type) : null;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openManageDialog = () => {
    setManagedTypes(configuredSolutions.map(s => s.type));
    setIsDropdownOpen(false);
    setIsManageOpen(true);
  };

  const toggleSolutionType = (typeId) => {
    setManagedTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleManageSave = () => {
    if (managedTypes.length === 0) return;

    const newSolutions = solutionTypes
      .filter(t => managedTypes.includes(t.id))
      .map(t => {
        const existing = configuredSolutions.find(s => s.type === t.id);
        return existing ?? {
          id: `${t.id}-${Date.now()}`,
          type: t.id,
          name: `${t.name} Dashboard`,
          isDefault: false,
        };
      });

    setConfiguredSolutions(newSolutions);
    setIsManageOpen(false);

    if (!managedTypes.includes(currentSolutionType) && newSolutions.length > 0) {
      const typeInfo = getSolutionTypeInfo(newSolutions[0].type);
      if (typeInfo?.route) navigate(typeInfo.route);
    }
  };

  const handleSolutionSelect = (solutionId) => {
    setIsDropdownOpen(false);
    const solution = configuredSolutions.find(s => s.id === solutionId);
    if (solution) {
      const typeInfo = getSolutionTypeInfo(solution.type);
      if (typeInfo?.route) navigate(typeInfo.route);
    }
  };

  return (
    <>
      <div className="env-dashboard-header">
        <div className="env-dashboard-header-content">
          <div className="env-dashboard-header-left">
            <h1 className="env-dashboard-title">
              {currentSolution ? currentSolution.name : 'Dashboard'}
            </h1>
            <p className="env-dashboard-subtitle">
              {currentTypeInfo ? currentTypeInfo.fullName : 'Select a solution to monitor'}
            </p>
          </div>

          {/* Settings Button */}
          {settingsRoute && (
            <button
              className="header-settings-btn"
              onClick={() => navigate(settingsRoute)}
              title="Dashboard Settings"
            >
              <FiSettings size={18} />
            </button>
          )}

          {/* Solution Dropdown */}
          <div className="solution-dropdown-container" ref={dropdownRef}>
            <button
              className="solution-dropdown-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              {currentTypeInfo && (
                <currentTypeInfo.icon size={20} color={currentTypeInfo.color} />
              )}
              <span>{currentSolution ? currentSolution.name : 'Select Solution'}</span>
              <FiChevronDown
                size={18}
                className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`}
              />
            </button>

            {isDropdownOpen && (
              <div className="solution-dropdown-menu">
                <div className="dropdown-section-label">Configured Solutions</div>
                {configuredSolutions.map((solution) => {
                  const typeInfo = getSolutionTypeInfo(solution.type);
                  const isActive = solution.type === currentSolutionType;
                  return (
                    <button
                      key={solution.id}
                      className={`dropdown-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleSolutionSelect(solution.id)}
                    >
                      {typeInfo && <typeInfo.icon size={18} color={typeInfo.color} />}
                      <span>{solution.name}</span>
                      {solution.isDefault && <span className="default-badge">Default</span>}
                    </button>
                  );
                })}
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item manage-solutions-btn"
                  onClick={openManageDialog}
                >
                  <FiSettings size={16} />
                  <span>Manage Solutions</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manage Solutions – DialogBox */}
      <DialogBox
        isOpen={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        title="Manage Solutions"
        onSave={handleManageSave}
        saveLabel="Apply Changes"
        cancelLabel="Cancel"
      >
        <div className="manage-dialog-sub">
          Select which solutions appear in the menu &nbsp;·&nbsp;
          <strong>{managedTypes.length}</strong> of {solutionTypes.length} active
        </div>

        <div className="manage-solutions-list">
          {solutionTypes.map(type => {
            const isOn = managedTypes.includes(type.id);
            const isCurrent = type.id === currentSolutionType;
            return (
              <div
                key={type.id}
                className={`manage-solution-card ${isOn ? 'msc-on' : 'msc-off'}`}
                onClick={() => toggleSolutionType(type.id)}
              >
                <div className="msc-icon" style={{ background: `${type.color}18`, color: isOn ? type.color : "#94a3b8" }}>
                  <type.icon size={22} />
                </div>
                <div className="msc-info">
                  <div className="msc-name" style={{ color: isOn ? "#1e293b" : "#94a3b8" }}>
                    {type.name} Dashboard
                    {isCurrent && <span className="msc-current-badge">Current</span>}
                  </div>
                  <div className="msc-fullname">{type.fullName}</div>
                </div>
                <div className={`msc-toggle ${isOn ? 'msc-toggle-on' : ''}`}
                  style={isOn ? { background: type.color } : {}}>
                  <div className="msc-toggle-knob" />
                </div>
              </div>
            );
          })}
        </div>
      </DialogBox>
    </>
  );
};

export default DashboardHeader;
